import {
  Calendar,
  ClipboardList,
  History,
  Home,
  LogOut,
  QrCode,
  Ticket,
  TicketCheck,
  X,
} from "lucide-react";
import React from "react";
import { NavLink } from "react-router-dom";
import { Button } from "../../components/button";
import { useAuthSession } from "../auth/shared";

interface MerchantSidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  onLogout: () => void;
}

const MerchantSidebar: React.FC<MerchantSidebarProps> = ({
  sidebarOpen,
  setSidebarOpen,
  onLogout,
}) => {
  const { user } = useAuthSession();

  return (
    <aside
      className={`fixed z-40 inset-y-0 left-0 w-60 bg-white flex flex-col transition-transform duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} dark:bg-gray-900`}
    >
      {/* Header Section */}
      <div className="flex items-center h-16 min-h-16 px-4 border-b border-l-0 border-r-0 border-[#e2e8f0] dark:border-gray-800">
        <div className="flex items-center space-x-3">
          {/* City Logo SVG */}
          <div className="flex items-center justify-center">
            <svg
              width="27"
              height="31"
              viewBox="0 0 27 31"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.5563 26.4705C10.8071 26.4705 9.16734 26.1425 7.63684 25.4866C6.13759 24.8307 4.81012 23.9249 3.65444 22.7692C2.49876 21.5823 1.59296 20.2236 0.937036 18.6931C0.312345 17.1314 0 15.4603 0 13.68C0 11.3061 0.546604 9.16658 1.63981 7.26128C2.76426 5.32474 4.29475 3.77863 6.23129 2.62295C8.19906 1.46727 10.4167 0.889431 12.8842 0.889431C15.1019 0.858196 17.0853 1.24863 18.8344 2.06073C20.5836 2.87282 21.9735 3.99727 23.0042 5.43406L18.1316 9.41646C17.5694 8.57313 16.8354 7.90158 15.9296 7.40183C15.055 6.90208 14.0712 6.6522 12.9779 6.6522C11.6349 6.6522 10.4636 6.98016 9.46406 7.63609C8.46456 8.26078 7.68369 9.10411 7.12147 10.1661C6.59049 11.2281 6.32499 12.3994 6.32499 13.68C6.32499 14.9294 6.6061 16.1006 7.16832 17.1939C7.76178 18.2558 8.55826 19.1148 9.55777 19.7707C10.5885 20.3954 11.7754 20.7077 13.1185 20.7077C14.3367 20.7077 15.3674 20.4422 16.2107 19.9113C17.0541 19.349 17.7881 18.6463 18.4128 17.8029L23.0042 21.7853C21.8486 23.2534 20.4118 24.409 18.6939 25.2524C17.0072 26.0645 14.9613 26.4705 12.5563 26.4705Z"
                fill="#00C49A"
              />
              <path
                d="M13.1746 30.3124L20.7566 23.9874H5.09748L13.1746 30.3124Z"
                fill="#00C49A"
              />
              <path
                d="M20.2866 16.1613C21.0362 16.8797 21.942 17.2389 23.004 17.2389C24.0972 17.2389 25.003 16.8797 25.7214 16.1613C26.4398 15.4429 26.799 14.5527 26.799 13.4907C26.799 12.4912 26.4398 11.6323 25.7214 10.9139C25.003 10.1642 24.0972 9.78943 23.004 9.78943C21.942 9.78943 21.0362 10.1642 20.2866 10.9139C19.5682 11.6323 19.209 12.4912 19.209 13.4907C19.209 14.5215 19.5682 15.4116 20.2866 16.1613Z"
                fill="#FEC057"
              />
            </svg>
          </div>

          {/* Horizontal Divider */}
          <div className="flex items-center justify-center">
            <svg
              width="2"
              height="20"
              viewBox="0 0 2 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="sidebar-divider"
            >
              <rect
                x="1.29883"
                y="0.0999756"
                width="19"
                height="0.999999"
                transform="rotate(90 1.29883 0.0999756)"
                fill="black"
                fillOpacity="0.1"
              />
            </svg>
          </div>

          <span
            className="text-base font-normal text-[#004232] dark:text-white"
            style={{ fontFamily: "Cal Sans" }}
          >
            {user?.organization_name || "Business Name"}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(false)}
          className="ml-auto lg:hidden"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-4 space-y-1 overflow-y-auto border-r">
        {/* Home */}
        <NavLink
          to="/merchant/home"
          end
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          }
        >
          <Home className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">Home</span>
        </NavLink>

        {/* Coupons */}
        <NavLink
          to="/merchant/coupons"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          }
        >
          <Ticket className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">Coupons</span>
        </NavLink>

        {/* Events */}
        <NavLink
          to="/merchant/events"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          }
        >
          <Calendar className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">Events</span>
        </NavLink>

        {/* Coupon Redeemer */}
        <NavLink
          to="/merchant/coupon-redeemer"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          }
        >
          <TicketCheck className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">Coupon Redeemer</span>
        </NavLink>

        {/* QR Scanner */}
        <NavLink
          to="/merchant/qr-scanner"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          }
        >
          <QrCode className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">QR Scanner</span>
        </NavLink>

        {/* Surveys */}
        <NavLink
          to="/merchant/surveys"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          }
        >
          <ClipboardList className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">Surveys</span>
        </NavLink>

        {/* Reward History */}
        <NavLink
          to="/merchant/reward-history"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          }
        >
          <History className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">Reward History</span>
        </NavLink>

      </nav>

      {/* Logout Section */}
      <div className="p-4 border-r border-t border-[#e2e8f0] dark:border-gray-700">
        <Button
          variant="ghost"
          className="w-full justify-start text-[#4A5565] hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800 px-3 py-2 rounded-[10px]"
          onClick={onLogout}
        >
          <LogOut className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">Logout</span>
        </Button>
      </div>
    </aside>
  );
};

export default MerchantSidebar;
